import React, { useContext, useEffect, useRef, useState } from 'react';
import GameLayout from './GameLayout';
import BombCounter, { BombCounterRef } from './BombCounter';
import Timer, { TimerRef } from './Timer';
import SmileButton, { SmileButtonRef } from './SmileButton';
import Tiles, { TilesRef } from './Tiles';
import { GameSettingsContext } from '../../providers/GameSettingProvider';
import { GameInterfaceContext } from '../../providers/GameInterfaceProvider';
import Alert from '../game-interface/common/Alert';
import { Firework } from '../game-interface/Firework';
import styled from 'styled-components';
import FullScreenAd from './FullScreenAd';

const Wrapper = styled.div`
  position: relative;
`;

export interface ClickHandles {
	handleMouseDown: (event: React.MouseEvent<HTMLDivElement>) => void;
	handleMouseUp: (event: React.MouseEvent<HTMLDivElement>) => void;
	handleLeftClick: (event: React.MouseEvent<HTMLDivElement>) => void;
	handleRightClick: (event: React.MouseEvent<HTMLDivElement>) => void;
}

const Game = () => {
	const { gameSettings } = useContext(GameSettingsContext);
	const { gameInterface } = useContext(GameInterfaceContext);
	const timerRef = useRef<TimerRef>(null);
	const smileButtonRef = useRef<SmileButtonRef>(null);
	const bombCounterRef = useRef<BombCounterRef>(null);
	const tilesRef = useRef<TilesRef>(null);
	const [timeIsUp, setTimeIsUp] = useState(false);

	const isFirstClick = useRef(true);
	const isMouseDownRef = useRef(false);

	const [isWin, setIsWin] = useState(false);
	const [isFullScreenAd, setIsFullScreenAd] = useState(false);
	const [date, setDate] = useState(new Date().getTime());


	useEffect((): void => {
		resetGame();
	}, [gameSettings, gameInterface.isVisible]);

	useEffect((): void => {
		if (timeIsUp) {
			loseGame();
		}
	}, [timeIsUp]);

	const handleMouseDown = () => {
		isMouseDownRef.current = true;
		smileButtonRef.current?.setRole('wonder');
	};

	const handleMouseUp = () => {
		isMouseDownRef.current = false;
		smileButtonRef.current?.setRole('default');
	};

	const handleRightClick = (event: React.MouseEvent<HTMLDivElement>) => {
		event.preventDefault();

		const tile = tilesRef.current?.getTileByClick(event);

		if (tile && tile.isClosed()) {
			const clickCountRef = tile.getRightClickCount();
			const clickCount = clickCountRef.current;

			if (clickCount == 0 && bombCounterRef.current?.isBombsExist()) {
				tile.setTileClosedRole('flag');
				tilesRef.current?.saveFlag(tile);
				clickCountRef.current++;
				bombCounterRef.current?.dec();
			} else if (clickCount == 1) {
				tile.setTileClosedRole('question');
				tilesRef.current?.removeFlag(tile);
				clickCountRef.current++;
				bombCounterRef.current?.inc();
			} else if (clickCount == 2) {
				tile.setTileClosedRole('empty');
				clickCountRef.current = 0;
			}
		}
	};

	const handleLeftClick = (event: React.MouseEvent<HTMLDivElement>) => {
		const tile = tilesRef.current?.getTileByClick(event);
		if (tile && tile.isLeftClicable()) {
			if (isFirstClick.current) {
				startGame(event);
				isFirstClick.current = false;
			}

			tilesRef.current?.openDependentTiles(tile);

			if (tile.isBomb()) {
				tile.setAccent();
				loseGame();
			}

			if (tilesRef.current?.isAllNoBombOpened()) winGame();
		}
	};

	const startGame = (event: React.MouseEvent<HTMLDivElement>) => {
		tilesRef.current?.initTiles(event);
		tilesRef.current?.enableTiles();
		timerRef.current?.start();
		setTimeIsUp(false);
	};

	const endGame = () => {
		timerRef.current?.stop();
		tilesRef.current?.disableTiles();
	};

	const loseGame = () => {
		tilesRef.current?.showErrors();
		smileButtonRef.current?.setRole('sad');
		endGame();
		showAd();
	};

	const winGame = (): void => {
		smileButtonRef.current?.setRole('sunglasses');
		setIsWin(true);
		endGame();
		showAd();
	};

	const resetGame = () => {
		endGame();
		tilesRef.current?.resetTiles();
		tilesRef.current?.enableTiles();
		isFirstClick.current = true;
		timerRef.current?.reset();
		smileButtonRef.current?.setRole('default');
		bombCounterRef.current?.reset();
		setIsWin(false);
		setTimeIsUp(false);
	};

	const showAd = (): void => {
		const now = new Date().getTime();
		if (now - date > 60000) {
			setDate(now);
			setIsFullScreenAd(true);
		}
	};

	const clickHandles: ClickHandles = {
		handleMouseDown,
		handleMouseUp,
		handleLeftClick,
		handleRightClick,
	};

	return (
		<>
			{isFullScreenAd && <FullScreenAd callback={setIsFullScreenAd}/>}
			<Wrapper>
				<GameLayout
					tiles={
						<Tiles
							ref={tilesRef}
							clickHandles={clickHandles}
						/>
					}
					bombCounter={
						<BombCounter
							ref={bombCounterRef}
							initialBombCount={gameSettings.mineCount}
						/>
					}
					timer={<Timer ref={timerRef} setTimeIsUp={setTimeIsUp}/>}
					smileButton={
						<SmileButton ref={smileButtonRef} handleClick={resetGame}/>
					}
					alert={<Alert isVisible={timeIsUp}/>}
				/>
				{isWin &&
					<>
						<Firework/>
						<Firework/>
						<Firework/>
					</>
				}
			</Wrapper>
		</>
	);
};

export default Game;
